import IconeAjuda from '@/global/components/IconeAjuda'
import Menu from '@/global/components/MenuV2'
import Grid from '@/global/components/ui/Grid'
import { encodeFile } from '@acme/common'
import dayjs from 'dayjs'
import { FormField, FormFieldCheckbox, FormFile } from 'global/components/Form'
import { SelectEmpresa } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useCallback, useEffect } from 'react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { afd } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'

const schema = z.object({
    afd: z.instanceof(File, { message: 'Selecione um arquivo para importar' }),
    dataInicial: z.string().nonempty('Data inicial é obrigatória'),
    dataFinal: z.string().nonempty('Data final é obrigatória'),
    tipoDocumento: z.enum(['cpf', 'pis', 'id', 'matricula']),
    isPortaria671: z.boolean().optional(),
})

type FormData = z.infer<typeof schema>

export function ImportarAFD() {
    const empresaId = useStore(state => state.companyId)
    const { mutateAsync, isLoading } = afd.mutations.useImport()

    const handleSubmit = useCallback<SubmitHandler<FormData>>(
        async ({
            afd: file,
            dataInicial,
            dataFinal,
            tipoDocumento,
            isPortaria671,
        }) => {
            if (!file) return toast.error('Selecione um arquivo para importar')

            const data = {
                file: await encodeFile(file),
                dataInicial,
                dataFinal,
                empresaId,
                ...(isPortaria671
                    ? { isPortaria671: true, tipoDocumento: 'cpf' }
                    : { tipoDocumento }),
            }

            return mutateAsync(data as any, {
                onSuccess: historico => {
                    afd.fillHistoricos(historico)
                    toast.success('Arquivo importado com sucesso!')
                },
                onError: err => {
                    toast.error(err.message)
                },
            })
        },
        [empresaId],
    )

    return (
        <Dialog.Root
            width="34rem"
            height="auto"
            open={hasPermission(['create', 'AFD'])}
            onSubmit={handleSubmit}
            defaultValues={{
                dataInicial: dayjs().startOf('month').format('YYYY-MM-DD'),
                dataFinal: dayjs().endOf('month').format('YYYY-MM-DD'),
                tipoDocumento: 'cpf',
                isPortaria671: true,
            }}
            schema={schema}
            loading={isLoading}
        >
            <Dialog.Title>Importar AFD</Dialog.Title>
            <Dialog.Content dividers>
                <Form />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit>
                    Importar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

function Form() {
    const { watch, setValue } = useFormContext<FormData>()
    const isPortaria671 = watch('isPortaria671')
    const tipoDocumento = watch('tipoDocumento')

    useEffect(() => {
        if (isPortaria671 && tipoDocumento === 'pis') {
            setValue('tipoDocumento', 'cpf')
        }
    }, [isPortaria671])

    return (
        <Grid container columnSpacing={2} className="items-center">
            <Grid item xs={12}>
                <SelectEmpresa width="100%" size="medium" />
            </Grid>
            <Grid item xs={12}>
                <span className="text-xs mt-4 mb-2">
                    Período de importação dos registros
                </span>
            </Grid>
            <Grid item xs={6}>
                <FormField name="dataInicial" label="Inicio" type="date" />
            </Grid>
            <Grid item xs={6}>
                <FormField name="dataFinal" label="Final" type="date" />
            </Grid>
            <Grid item xs={12}>
                <FormFieldCheckbox
                    name="isPortaria671"
                    label="Utiliza padrão de layout da portaria 671"
                />
            </Grid>
            <Grid item xs={12}>
                <FormField
                    select
                    name="tipoDocumento"
                    label="Tipo de identificação dos funcionários"
                    InputProps={{
                        endAdornment: (
                            <IconeAjuda className="mr-4">
                                Utilizado para identificar os funcionários no
                                arquivo AFD. Caso a opção "Utiliza padrão de
                                layout da portaria 671" esteja marcada, não será
                                possível selecionar PIS.
                                <br />
                                <br />
                                Para importar arquivos do IdFace, selecione a
                                opção "ID" ou "Matrícula".
                            </IconeAjuda>
                        ),
                    }}
                >
                    {[
                        { label: 'CPF', value: 'cpf', disabled: false },
                        {
                            label: 'PIS',
                            value: 'pis',
                            disabled: isPortaria671,
                        },
                        {
                            label: 'ID',
                            value: 'id',
                            disabled: false,
                        },
                        {
                            label: 'Matrícula',
                            value: 'matricula',
                            disabled: false,
                        },
                    ].map(item => (
                        <Menu.Item
                            key={item.value}
                            value={item.value}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </Menu.Item>
                    ))}
                </FormField>
            </Grid>
            <Grid item xs={12} className="mt-4">
                <FormFile name="afd" notPreview accept="text/plain" />
            </Grid>
        </Grid>
    )
}
